import { ConfigurationContext } from 'components/provider/OrginazationContext';
import ConfirmationModal from 'components/ui/ConfirmationModal/confirmation-modal';
import Container from 'components/ui/Container';
import CustomSelect from 'components/ui/Editor/CustomSelect';
import Modal from 'components/ui/Modal/modal';
import PrimaryButton from 'components/ui/PrimaryButton';
import SecondaryButton from 'components/ui/SecondaryButton';
import useToggle from 'hooks/useToggle';
import { useContext, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDeleteGalleryReportMutation, useListGalleryByFolderQuery } from 'redux/reducers/gallery';
import { backendURL } from 'redux/reducers/settings';
import { useAddMultipleImages, useAssignImageToRoutine, useGetAllRoutineList } from 'service/admin.query';
import { handleCopy } from 'utils/copy';

const PAGE_SIZE = 20;
const MAX_IMAGE_SIZE = 1 * 1024 * 1024; // 1MB per image

const RoutineCards = () => {
    const navigate = useNavigate();
    const { data: routineList, isLoading } = useGetAllRoutineList();
    const { appConfig } = useContext(ConfigurationContext);

    return (
        <Container title="Gallery" subTitle="Select a routine to view its images">
            <div className="row mt-4">
                {isLoading && <p className="text-center">Loading routines...</p>}
                {!isLoading && !routineList?.length && <p className="text-center">No routines found</p>}
                
                {routineList?.map((routine: any) => (
                    <div key={routine.routine_master_id} className="col-md-3 col-sm-6 mb-4">
                        <div
                            className="card h-100 shadow-sm border-0"
                            style={{ cursor: 'pointer', overflow: 'hidden', transition: 'transform 0.2s' }}
                            onClick={() => navigate(`/gallery/${routine.routine_master_id}`)}
                            onMouseOver={(e) => (e.currentTarget.style.transform = 'scale(1.02)')}
                            onMouseOut={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                        >
                            {/* Image at the top */}
                            <img 
                                src="/gallery-folder.png" 
                                className="card-img-top" 
                                alt="Folder"
                                style={{ 
                                    height: '100px', 
                                    objectFit: 'cover',
                                    backgroundColor: '#f8f9fa' // light background fallback
                                }} 
                            />

                            {/* Routine details below the image */}
                            <div className="card-body">
                                <h5 className="card-title fw-bold text-dark mb-2" style={{ fontSize: '1.1rem' }}>
                                    {routine.training_name}
                                </h5>
                                <div className="text-muted fs-14">
                                    <p className="mb-1">
                                        <i className="bi bi-people me-1"></i> {routine.batch_name}
                                    </p>
                                    <p className="mb-0 text-truncate">
                                        <i className="bi bi-geo-alt me-1"></i> {routine.hall_name}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </Container>
    );
};

const GalleryImages = () => {
    const { routineId } = useParams<{ routineId: string }>();
    const navigate = useNavigate();

    const [showAssignModal, toggleAssignModal] = useToggle(false);
    const [selectedRoutine, setselectedRoutine] = useState<any>();
    const [img, setImg] = useState<any>();
    const [page, setPage] = useState(1);
    const [selectedImages, setSelectedImages] = useState<string[]>([]);

    const {
        data: galleryList,
        isLoading,
        refetch
    } = useListGalleryByFolderQuery({
        folderName: 'report',
        page,
        pageSize: PAGE_SIZE,
        routineId: routineId ?? undefined
    });

    const [deleteImage, { isLoading: isDeleting }] = useDeleteGalleryReportMutation();
    const [selectedImage, setSelectedImage] = useState<string | null>(null);
    const [deleteModal, toggleDeleteModal] = useToggle(false);

    const { data: activeRoutine } = useGetAllRoutineList();
    const { mutate: assignImageToRoutine, isLoading: isAssigning } = useAssignImageToRoutine();
    const { mutate: addMultipleImages, isLoading: isUploading } = useAddMultipleImages();
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleUploadImages = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        // reset the input so selecting the same files again still triggers change
        if (fileInputRef.current) fileInputRef.current.value = '';

        if (!files.length) return;

        const oversized = files.filter((f) => f.size > MAX_IMAGE_SIZE);
        if (oversized.length) {
            toast.error(
                `Each image must be 1MB or less. Skipped: ${oversized.map((f) => f.name).join(', ')}`
            );
        }

        const validFiles = files.filter((f) => f.size <= MAX_IMAGE_SIZE);
        if (!validFiles.length) return;

        addMultipleImages(
            {
                files: validFiles,
                routine_id: routineId,
                folder_name: 'report'
            },
            {
                onSuccess: (res: any) => {
                    if (res?.data?.code == 200) {
                        toast.success(res?.data?.message || 'Images uploaded successfully');
                        refetch();
                    } else {
                        toast.error(res?.data?.message || 'Upload failed');
                    }
                },
                onError: (err: any) => {
                    toast.error(err?.data?.message || 'Upload failed');
                }
            }
        );
    };

    const total: number = (galleryList?.data?.images?.[0] as any)?.row_total ?? 0;
    const totalPages = Math.ceil(total / PAGE_SIZE);
    const canNext = page < totalPages;
    const canPrevious = page > 1;

    const currentRoutine = activeRoutine?.find(
        (r: any) => String(r.routine_master_id) === String(routineId)
    );

    const onDeleteConfirm = () => {
        selectedImage &&
            deleteImage(selectedImage)
                .unwrap()
                .then((data: any) => {
                    if (data.code == 200) {
                        toast.success('Deleted Successfully');
                        setSelectedImage(null);
                        refetch();
                        toggleDeleteModal();
                    } else {
                        toast.error(data.message || 'Error');
                    }
                })
                .catch(() => {
                    toast.error('Error');
                });
    };

    const onDeleteClicked = (path: string) => {
        setSelectedImage(path);
        toggleDeleteModal();
    };

    const unassignedImages = galleryList?.data?.images?.filter((i: any) => !i.is_assigned) ?? [];
    const allSelected =
        unassignedImages.length > 0 &&
        unassignedImages.every((i: any) => selectedImages.includes(i.path));

    const toggleImageSelection = (path: string) => {
        setSelectedImages(prev =>
            prev.includes(path) ? prev.filter(p => p !== path) : [...prev, path]
        );
    };

    const handleSelectAll = () => {
        if (allSelected) {
            setSelectedImages([]);
        } else {
            setSelectedImages(unassignedImages.map((i: any) => i.path));
        }
    };

    const routineTitle = currentRoutine
        ? `${currentRoutine.training_name} — ${currentRoutine.batch_name}`
        : 'Gallery';

    return (
        <Container title={routineTitle} subTitle="Click on image to copy image url">
            <ConfirmationModal
                isOpen={deleteModal}
                onClose={toggleDeleteModal}
                onConfirm={onDeleteConfirm}
                title="Delete Confirmations"
                isLoading={isDeleting}
            >
                <>
                    <div className="mt-4 pt-2 fs-15 mx-4 mx-sm-5">
                        <h4>Are you Sure ?</h4>
                        <p className="text-muted">You want to Remove this Record ?</p>
                    </div>
                </>
            </ConfirmationModal>

            <Modal
                title="Assign Image to Routine"
                isModalOpen={showAssignModal}
                closeModal={() => toggleAssignModal(false)}
                modalSize="modal-md"
                renderFooter={() => (
                    <div className="hstack gap-2 justify-content-end">
                        <SecondaryButton onClick={() => toggleAssignModal(false)} title="Cancel" />
                        <PrimaryButton
                            type="button"
                            title="Assign"
                            onClick={() => {
                                const paths = img ? [img.path] : selectedImages;
                                const payload = {
                                    routine_id: selectedRoutine,
                                    image_paths: paths
                                };
                                assignImageToRoutine(payload, {
                                    onSuccess: (res) => {
                                        if (res?.data.code == 200) {
                                            toast.success(res.data.message);
                                            toggleAssignModal(false);
                                            setSelectedImages([]);
                                            refetch();
                                        } else {
                                            toast.error(res?.data?.message || 'Error');
                                        }
                                    }
                                });
                            }}
                            isLoading={isAssigning}
                        />
                    </div>
                )}
            >
                <div>
                    <h4 className="fw-bold">
                        {img
                            ? 'Assign image to routine?'
                            : `Assign ${selectedImages.length} selected image(s) to routine?`}
                    </h4>
                    <div className="col-12">
                        <CustomSelect
                            containerClassName="col-12"
                            options={activeRoutine?.map((item: any) => ({
                                label: `${item?.training_name} - ${item?.batch_name} - ${item?.hall_name}`,
                                value: item?.routine_master_id
                            }))}
                            placeholder="Routine (training name - batch name - hall name)"
                            value={selectedRoutine}
                            onChange={(value: any) => setselectedRoutine(value)}
                            label="Select Routine you want to assign"
                        />
                    </div>
                </div>
            </Modal>

            <div className="mb-3 d-flex align-items-center justify-content-between flex-wrap gap-2">
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => navigate('/gallery')}
                >
                    ← Back to Routines
                </button>

                <div>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="d-none"
                        onChange={handleUploadImages}
                    />
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        disabled={isUploading}
                        onClick={() => fileInputRef.current?.click()}
                        title="Each image must be 1MB or less"
                    >
                        {isUploading ? 'Uploading...' : '+ Add Images'}
                    </button>
                    <small className="text-muted ms-2">Max 1MB per image</small>
                </div>
            </div>

            <div>
                {unassignedImages.length > 0 && (
                    <div className="d-flex align-items-center gap-3 mb-3">
                        <div className="form-check mb-0">
                            <input
                                className="form-check-input"
                                type="checkbox"
                                id="selectAll"
                                checked={allSelected}
                                onChange={handleSelectAll}
                            />
                            <label className="form-check-label fw-semibold" htmlFor="selectAll">
                                Select All ({unassignedImages.length} unassigned)
                            </label>
                        </div>
                        {selectedImages.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-primary btn-sm"
                                onClick={() => {
                                    setImg(null);
                                    toggleAssignModal(true);
                                }}
                            >
                                Assign Selected ({selectedImages.length})
                            </button>
                        )}
                        {selectedImages.length > 0 && (
                            <button
                                type="button"
                                className="btn btn-outline-secondary btn-sm"
                                onClick={() => setSelectedImages([])}
                            >
                                Clear Selection
                            </button>
                        )}
                    </div>
                )}

                <div className="row mt-4">
                    {isLoading && <p>Loading images...</p>}
                    {!isLoading && galleryList?.data?.images?.length
                        ? galleryList.data.images.map((imgItem: any, index: number) => {
                            const imageUrl = backendURL + imgItem.path;
                            const isChecked = selectedImages.includes(imgItem.path);

                            return (
                                <div key={index} className="col-md-3 mb-4">
                                    <div
                                        className={`position-relative border rounded p-2 ${isChecked ? 'border-primary border-2' : ''}`}
                                        style={{ cursor: !imgItem?.is_assigned ? 'pointer' : 'default' }}
                                    >
                                        {!imgItem?.is_assigned && (
                                            <input
                                                type="checkbox"
                                                className="position-absolute form-check-input"
                                                style={{
                                                    top: '8px',
                                                    left: '8px',
                                                    width: '18px',
                                                    height: '18px',
                                                    zIndex: 10,
                                                    cursor: 'pointer'
                                                }}
                                                checked={isChecked}
                                                onChange={() => toggleImageSelection(imgItem.path)}
                                            />
                                        )}
                                        {!imgItem?.is_assigned && (
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm position-absolute top-0 start-0 mt-2"
                                                style={{ marginLeft: '32px' }}
                                                onClick={() => {
                                                    setImg(imgItem);
                                                    toggleAssignModal(true);
                                                }}
                                                title="Assign"
                                            >
                                                Assign
                                            </button>
                                        )}
                                        <img
                                            src={imageUrl}
                                            alt="gallery"
                                            className="img-fluid rounded"
                                            style={{ height: '200px', objectFit: 'cover', width: '100%' }}
                                            onClick={() => handleCopy(imageUrl)}
                                            title="Click to copy image path"
                                        />
                                        <button
                                            type="button"
                                            className="btn btn-danger btn-sm position-absolute top-0 end-0 m-2"
                                            onClick={() => onDeleteClicked(imgItem.path)}
                                            title="Delete"
                                        >
                                            ✕
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                        : !isLoading && <p>No images found</p>}
                </div>

                {totalPages > 1 && (
                    <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            disabled={!canPrevious}
                            onClick={() => setPage(p => p - 1)}
                        >
                            Previous
                        </button>
                        <span className="fs-14">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            className="btn btn-outline-secondary btn-sm"
                            disabled={!canNext}
                            onClick={() => setPage(p => p + 1)}
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>
        </Container>
    );
};

const GalleryList = () => {
    const { routineId } = useParams<{ routineId: string }>();

    if (!routineId) return <RoutineCards />;
    return <GalleryImages />;
};

export default GalleryList;
